/**
 * ¿Esto corre DENTRO de la app nativa o en un navegador?
 *
 * **Éste es el único lugar que hace esa pregunta.** La app de iPhone es esta
 * misma web adentro de un contenedor Capacitor (ver `docs/PLAN_IOS_CAPACITOR.md`),
 * así que el 99% del código no tiene que enterarse. Lo que sí cambia —el
 * origen de los links, la puerta biométrica, los avisos de "agregá a inicio"—
 * pregunta acá y no con un chequeo propio: chequeos sueltos por pantalla es
 * exactamente cómo se llega a que la mitad de la app crea que está en un
 * navegador y la otra mitad no.
 *
 * NO se importa `@capacitor/core` a propósito. El contenedor inyecta
 * `window.Capacitor` antes de que cargue el bundle, así que alcanza con
 * mirarlo, y de paso el bundle de la web no engorda ni queda atado a un
 * paquete que en el navegador no hace nada.
 */

type CapacitorGlobal = {
  isNativePlatform?: () => boolean;
  /** Capacitor 3 y anteriores. */
  isNative?: boolean;
  getPlatform?: () => string;
  platform?: string;
  isPluginAvailable?: (nombre: string) => boolean;
  /**
   * El puente crudo: pedir un método nativo sin pasar por el paquete JS del
   * plugin. Lo define el `native-bridge.js` que el contenedor inyecta ANTES de
   * que cargue el bundle, así que está disponible desde el primer pintado.
   */
  nativePromise?: (plugin: string, metodo: string, opciones?: unknown) => Promise<unknown>;
  /** Lo mismo con callback, que es como viajan los eventos. Devuelve el id del callback. */
  nativeCallback?: (
    plugin: string,
    metodo: string,
    opciones: unknown,
    callback: (dato: unknown, error?: unknown) => void,
  ) => string;
};

function capacitor(): CapacitorGlobal | undefined {
  if (typeof window === "undefined") return undefined;
  return (window as unknown as { Capacitor?: CapacitorGlobal }).Capacitor;
}

/** true sólo adentro de la app (iPhone hoy; Android nativo es Kotlin, no esto). */
export function esNativo(): boolean {
  const cap = capacitor();
  if (!cap) return false;
  if (typeof cap.isNativePlatform === "function") return cap.isNativePlatform();
  return cap.isNative === true;
}

/** "ios" | "android" | "web". Útil para lo que difiere entre tiendas. */
export function plataformaNativa(): string {
  const cap = capacitor();
  if (!cap) return "web";
  if (typeof cap.getPlatform === "function") return cap.getPlatform();
  return cap.platform ?? "web";
}

/** Atajo: la app de la App Store. */
export function esIosNativo(): boolean {
  return esNativo() && plataformaNativa() === "ios";
}

/**
 * ¿El contenedor registró este plugin en el puente?
 *
 * `null` cuando no se puede saber (en el navegador, o con un Capacitor viejo
 * que no expone `isPluginAvailable`); eso NO es lo mismo que `false`. La
 * diferencia importa en el diagnóstico del ingreso nativo: un plugin que el
 * puente no conoce explica que la llamada no vuelva nunca, y "no sé" no
 * explica nada.
 */
export function hayPluginNativo(nombre: string): boolean | null {
  const cap = capacitor();
  if (!cap || typeof cap.isPluginAvailable !== "function") return null;
  try {
    return cap.isPluginAvailable(nombre);
  } catch {
    return null;
  }
}

/**
 * Pedirle un método a un plugin POR EL PUENTE, sin el paquete JS del plugin.
 *
 * `null` cuando este Capacitor no expone el puente crudo — en el navegador
 * siempre, y en una versión vieja del contenedor también. No es un error: es
 * "por acá no se puede", y quien llama decide el plan B.
 *
 * Esto es la misma idea que el resto del archivo: el contenedor ya inyectó el
 * puente, así que alcanza con mirarlo. La diferencia es que acá además AHORRA
 * algo caro — el paquete JS del plugin es un chunk que hay que bajar y
 * evaluar en el momento del toque, y el 3-11 ese chunk no volvió nunca en un
 * iPhone con iOS 26.1 (ver `loginNativo.ts`). El puente, en la misma captura,
 * contestó en 2 ms.
 *
 * Lo que rechaza es un `Error` de verdad: el puente copia las propiedades del
 * error nativo sobre una excepción propia, así que `e.message` trae el texto
 * de iOS tal cual.
 */
export function llamarNativo<T>(
  plugin: string,
  metodo: string,
  opciones?: Record<string, unknown>,
): Promise<T> | null {
  const cap = capacitor();
  if (!cap || typeof cap.nativePromise !== "function") return null;
  try {
    return cap.nativePromise(plugin, metodo, opciones ?? {}) as Promise<T>;
  } catch (e) {
    // Que el puente explote al despachar es otra cosa que "no se puede": hay
    // que verlo, no caer al plan B en silencio.
    return Promise.reject(e);
  }
}

/**
 * Escuchar un evento de un plugin POR EL PUENTE: lo que hace `addListener`
 * del paquete, sin el paquete. Es exactamente lo que hace el runtime de
 * @capacitor/core por debajo —`nativeCallback(plugin, "addListener",
 * { eventName }, cb)`— copiado de ahí.
 *
 * `null` si el puente no está, igual que `llamarNativo`. Devuelve la función
 * que deja de escuchar.
 */
export function escucharNativo<T>(
  plugin: string,
  evento: string,
  escuchar: (dato: T) => void,
): (() => void) | null {
  const cap = capacitor();
  if (!cap || typeof cap.nativeCallback !== "function") return null;
  const callbackId = cap.nativeCallback(plugin, "addListener", { eventName: evento }, (dato, error) => {
    if (!error && dato != null) escuchar(dato as T);
  });
  return () => {
    void cap.nativePromise?.(plugin, "removeListener", { eventName: evento, callbackId });
  };
}
